import { BaseQueryApi } from '@reduxjs/toolkit/query';
import { buildStandardHeaders, getBaseUrl } from '../../ots-api';
import { createSseParser } from '../../../util/sse';
import { UserProcess, UserProcessCodec } from '../../../model/user-process';
import { validateJSONNow } from '../../../web/validation';

export type UserProcessStreamState = 'connecting' | 'open' | 'retrying' | 'closed';

export type UserProcessStreamHandlers = {
  /** A process changed: queued, made progress, finished, was canceled or marked seen. */
  onProcess: (process: UserProcess) => void;
  /** Reload the list: sent when the stream opens and after "mark all seen". */
  onRefresh: () => void;
  onStateChange?: (state: UserProcessStreamState) => void;
};

export type UserProcessStreamOptions = {
  /** Defaults to window.fetch; injectable for tests. */
  fetch?: typeof fetch;
  /** First reconnect delay; doubles up to {@link maxRetryMs}. */
  initialRetryMs?: number;
  maxRetryMs?: number;
};

export type UserProcessStreamConnection = {
  close: () => void;
};

/**
 * Opens `GET /processes/stream` with the session's auth and tenant headers and keeps it open, reconnecting with
 * backoff when it drops. `EventSource` can't send an Authorization header, so this reads the stream with fetch.
 */
export const connectUserProcessStream = (
  baseQueryApi: Pick<BaseQueryApi, 'getState' | 'extra' | 'type' | 'endpoint' | 'forced'>,
  handlers: UserProcessStreamHandlers,
  options: UserProcessStreamOptions = {}
): UserProcessStreamConnection => {
  const doFetch = options.fetch ?? ((...args: Parameters<typeof fetch>) => window.fetch(...args));
  const initialRetryMs = options.initialRetryMs ?? 1_000;
  const maxRetryMs = options.maxRetryMs ?? 30_000;

  let closed = false;
  let controller: AbortController | undefined;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  let retryMs = initialRetryMs;
  let serverRetryMs: number | undefined;

  const setState = (state: UserProcessStreamState) => handlers.onStateChange?.(state);

  const parser = createSseParser((event) => {
    if (event.retry !== undefined) serverRetryMs = event.retry;

    if (event.event === 'refresh') {
      handlers.onRefresh();
      return;
    }

    if (event.event === 'process') {
      try {
        handlers.onProcess(validateJSONNow(UserProcessCodec, JSON.parse(event.data)));
      } catch {
        // a malformed event shouldn't end the stream; the next refresh corrects the list
      }
    }
  });

  const scheduleReconnect = () => {
    if (closed) return;
    setState('retrying');
    const delay = Math.min(serverRetryMs ?? retryMs, maxRetryMs);
    retryMs = Math.min(retryMs * 2, maxRetryMs);
    retryTimer = setTimeout(connect, delay);
  };

  const connect = async () => {
    if (closed) return;

    controller = new AbortController();
    parser.reset();
    setState('connecting');

    try {
      const headers: Record<string, string> = {
        ...buildStandardHeaders(baseQueryApi),
        Accept: 'text/event-stream',
        'Cache-Control': 'no-cache',
      };

      const response = await doFetch(`${getBaseUrl(baseQueryApi as BaseQueryApi)}/processes/stream`, {
        method: 'GET',
        headers,
        signal: controller.signal,
      });

      if (!response.ok || !response.body) {
        scheduleReconnect();
        return;
      }

      setState('open');
      retryMs = initialRetryMs;

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        parser.push(decoder.decode(value, { stream: true }));
      }

      scheduleReconnect();
    } catch {
      if (!closed) scheduleReconnect();
    }
  };

  void connect();

  return {
    close: () => {
      closed = true;
      if (retryTimer) clearTimeout(retryTimer);
      controller?.abort();
      setState('closed');
    },
  };
};
